/**
 * LA SAISIE DES DOSSIERS CONCERNÉS — module PUR (écran de déclaration d'une NC).
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * « Lors de la déclaration d'une NC, je voudrais indiquer la liste des dossiers
 * patients impliqués. Je tape un numéro et j'appuie sur entrée, puis un autre,
 * puis un autre selon le besoin. » — RQ, 11/10/2026.
 *
 * Les LIGNES existaient déjà (`backend/src/nc-dossiers.ts`, RQ du 07/10/2026),
 * mais seulement sur la fiche ouverte, et sous la forme d'un champ par dossier
 * avec sa date : trois gestes de souris par numéro. Au moment de DÉCLARER, on a
 * les numéros sous les yeux et on les tape à la suite — d'où cette saisie-ci.
 *
 * ── Deux invariants ────────────────────────────────────────────────────────
 *
 * ⚠️ **AUCUN NUMÉRO NE SE PERD EN SILENCE.** Un numéro tapé et non validé par
 * « Entrée » est la faute la plus probable de cette saisie : l'écran reverse
 * donc la saisie en cours dans la liste au moment de créer (`ajouterDossiers`
 * est rejoué sur le texte resté dans le champ). Un champ vidé par l'envoi, sans
 * que son contenu soit arrivé quelque part, c'est la définition d'une perte
 * muette.
 *
 * ⚠️ **PAS DEUX FOIS LE MÊME DOSSIER.** Le compte des dossiers est DÉRIVÉ de
 * ces lignes (`compteRetenu`, moteur) : un doublon gonflerait le nombre de
 * dossiers concernés affiché au relevé du correspondant. Le doublon est donc
 * refusé, et surtout il est DIT — un numéro qui disparaît sans un mot se lit
 * comme un bug, et on le retape.
 *
 * ── Ce qu'il ne fait pas ───────────────────────────────────────────────────
 *
 * Il ne contrôle NI le format ni l'existence du numéro : la plateforme n'a pas
 * de lien au système informatique du laboratoire, et refuser « 2026-4411 »
 * parce qu'il ne ressemble pas à ce qu'on attend empêcherait d'enregistrer un
 * fait vrai. Le texte reste libre (décision du 07/10/2026), seulement borné en
 * longueur, comme côté moteur.
 *
 * Il ne pose PAS de date par ligne. La date de chaque ligne commande le relevé
 * mensuel du correspondant, et le moteur retombe sur la date de la
 * non-conformité quand la ligne n'en porte pas (`dossiersDuMois`) : au moment de
 * déclarer, c'est le bon repli. Les dates se précisent ligne par ligne sur la
 * fiche, où l'éditeur complet reste en place.
 */

/** Une ligne : un dossier concerné. Même forme que `DossierConcerne` du moteur. */
export interface LigneDossier {
  id: string;
  texte: string;
  date?: string;
}

/** Longueur maximale d'une ligne — la même que le moteur (`nc-dossiers.ts`), au
 *  caractère près : tronquer ici et laisser passer là-bas ferait afficher à
 *  l'écran autre chose que ce qui est enregistré. */
export const TEXTE_MAX = 200;

/** Les séparateurs d'un collage : retour à la ligne, virgule, point-virgule,
 *  tabulation. ⚠️ PAS l'espace — « dossier 2026-4411 — M. B. » est une ligne
 *  valide, et la découper en cinq rendrait la liste illisible. */
const SEPARATEURS = /[\n\r,;\t]+/;

const nettoyer = (v: unknown): string => String(v ?? '').replace(/\s+/g, ' ').trim().slice(0, TEXTE_MAX);

/** La clé de comparaison : à la casse et aux espaces près, « 2026-4411 » et
 *  « 2026-4411 » sont le même dossier. */
const cle = (v: string): string => nettoyer(v).toLowerCase();

/**
 * Découpe une saisie en numéros.
 *
 * Une seule frappe donne une seule ligne ; un COLLAGE (une colonne copiée du
 * SIL, une liste séparée par des virgules) donne autant de lignes qu'il porte de
 * numéros. Ne pas traiter le collage aurait fait une ligne unique de dix
 * numéros, qui ne compte que pour un au relevé.
 */
export function decouperSaisie(brut: unknown): string[] {
  return String(brut ?? '').split(SEPARATEURS).map(nettoyer).filter(Boolean);
}

/** Un identifiant libre dans la liste. Tiré d'un compteur et vérifié, JAMAIS de
 *  l'index : retirer la deuxième ligne de trois ferait sinon glisser les clés et
 *  React réutiliserait le champ de la troisième pour la deuxième. */
function idLibre(lignes: LigneDossier[]): string {
  const pris = new Set(lignes.map((l) => l.id));
  let n = lignes.length + 1;
  while (pris.has(`d${n}`)) n += 1;
  return `d${n}`;
}

export interface AjoutDossiers {
  /** La liste complète après ajout — jamais mutée sur place. */
  lignes: LigneDossier[];
  /** Ce qui vient d'entrer. Vide = rien n'a changé. */
  ajoutes: string[];
  /** Ce qui était déjà là, pour le dire. */
  doublons: string[];
}

/**
 * Ajoute à la liste ce qui a été tapé (ou collé).
 *
 * Rend TOUJOURS une liste utilisable, et distingue trois issues que l'écran doit
 * traiter différemment : des ajouts (on vide le champ), des doublons seuls (on
 * vide le champ ET on le dit), rien du tout (on ne touche à rien — un champ vidé
 * sans raison fait croire à un enregistrement).
 */
export function ajouterDossiers(lignes: LigneDossier[], saisie: unknown): AjoutDossiers {
  const out = [...lignes];
  const vus = new Set(out.map((l) => cle(l.texte)));
  const ajoutes: string[] = [];
  const doublons: string[] = [];
  for (const texte of decouperSaisie(saisie)) {
    if (vus.has(cle(texte))) { doublons.push(texte); continue; }
    vus.add(cle(texte));
    out.push({ id: idLibre(out), texte });
    ajoutes.push(texte);
  }
  return { lignes: out, ajoutes, doublons };
}

/** Retire une ligne. */
export function retirerDossier(lignes: LigneDossier[], id: string): LigneDossier[] {
  return lignes.filter((l) => l.id !== id);
}

/** Ce qu'on dit quand un numéro n'est pas entré. Vide quand il n'y a rien à
 *  dire : une phrase permanente sous un champ cesse d'être lue. */
export function phraseDoublons(doublons: string[]): string {
  if (!doublons.length) return '';
  return doublons.length === 1
    ? `${doublons[0]} est déjà dans la liste — il n’a pas été ajouté deux fois.`
    : `Déjà dans la liste, non ajoutés : ${doublons.join(', ')}.`;
}

/**
 * UN DOSSIER QUI RESSEMBLE À UN NOM (RQ, 14/10/2026).
 *
 * Le texte d'un dossier part TEL QUEL dans le relevé adressé au correspondant —
 * par courriel. On attend un NUMÉRO de dossier ; une saisie sans aucun chiffre,
 * faite de mots, est très probablement un nom de patient. On ne la refuse pas
 * (un libellé de lot sans chiffre existe), on AVERTIT.
 */
export function ressembleAUnNom(texte: unknown): boolean {
  const t = String(texte ?? '').trim();
  return !!t && !/\d/.test(t) && /\p{L}{2,}/u.test(t);
}

/** La phrase d'avertissement, ou `''` si aucune ligne ne ressemble à un nom. */
export function phraseNoms(lignes: readonly { texte: string }[]): string {
  const noms = lignes.map((l) => l.texte).filter(ressembleAUnNom);
  if (!noms.length) return '';
  return `${noms.map((n) => `« ${n} »`).join(', ')} ressemble${noms.length > 1 ? 'nt' : ''} à un nom : saisissez plutôt le numéro du dossier — ce texte part tel quel dans le relevé adressé au correspondant.`;
}

